// Import Swiper styles
import "swiper/css";
import "swiper/css/navigation";
import "styles/globals.css";

import { useEffect, useState } from "react";
import type { AppContext, AppProps } from "next/app";
import { default as NextApp } from "next/app";
import { useRouter } from "next/router";
import localFont from "next/font/local";

import Web3Provider from "@/client/components/common/Web3Provider";
import analytics from "@/client/lib/analytics";
import ReservoirMeta from "@/client/components/common/ReservoirMeta";
import AuthWatcher from "@/client/components/common/AuthWatcher";
import NewsletterBox from "@/client/components/common/NewsletterBox";
import { getBasePath, USE_PROXY, isBrowser } from "@/shared/config";
import { ExcessSettlementFundsManagerMachineContextProvider } from "@/client/components/common/ExcessSettlementFundsManagerContext/ExcessSettlementFundsManagerContextProvider";
import { Toaster } from "react-hot-toast";
import { PurchaseTrackingManagerMachineProvider } from "@/client/components/common/PurchaseTrackingManagerMachineContext/PurchaseTrackingManagerMachineProvider";
import { ArtBlocksProvider } from "@/client/components/common/ArtBlocksProvider";
import { AuthProvider } from "@/client/components/common/AuthProvider";
import { NavDataResponse } from "./api/nav-data";
import { Nav } from "@/client/components/common/Nav";
import NodeCache from "node-cache";
import { AB_FLAGSHIP_URL } from "@/client/lib/links";

function AppWrapper(
  props: AppProps & { baseUrl: string; navData: NavDataResponse }
) {
  return <MyApp {...props} />;
}

function MyApp({
  Component,
  pageProps,
  baseUrl,
  navData,
}: AppProps & { baseUrl: string; navData: NavDataResponse }) {
  const router = useRouter();

  const [darkMode, setDarkMode] = useState<boolean | any>(null);

  // Analytics
  useEffect(() => {
    // setup analytics on app load
    analytics.init();

    // track initial page load
    trackPageView({
      pathname: router.pathname,
      url: router.asPath,
      query: router.query,
    });

    const handleRouteChangeComplete = (
      url: string,
      options: { shallow: boolean }
    ) => {
      // dont track the page if we are shollow routing
      // most likely we are just updating query params
      if (options.shallow) return;

      trackPageView({
        pathname: router.pathname,
        url: url,
        query: router.query,
      });
    };

    // Record a pageview when route changes
    router.events.on("routeChangeComplete", handleRouteChangeComplete);

    // Unassign event listener
    return () => {
      router.events.off("routeChangeComplete", handleRouteChangeComplete);
    };
  }, []);

  useEffect(() => {
    localStorage.sansaTheme = "light"; // Force light mode
    document.documentElement.classList.remove("dark");
    setDarkMode(false);
  }, []);

  useEffect(() => {
    // when a user changes darkMode add it to localstorage
    if (darkMode === true) {
      // Whenever the user explicitly chooses light mode
      localStorage.sansaTheme = "dark";
      document.documentElement.classList.add("dark");
    } else if (darkMode === false) {
      // Whenever the user explicitly chooses light mode
      localStorage.sansaTheme = "light";
      document.documentElement.classList.remove("dark");
    }
  }, [darkMode]);

  return (
    <Web3Provider baseUrl={baseUrl}>
      <AuthProvider>
        <ArtBlocksProvider>
          <ExcessSettlementFundsManagerMachineContextProvider>
            <PurchaseTrackingManagerMachineProvider>
              <ReservoirMeta />
              <AuthWatcher />
              <Toaster />
              <style jsx global>{`
                :root {
                  --font-founders: ${founders.style.fontFamily};
                  --font-sohne: ${sohne.style.fontFamily};
              `}</style>
              <div className={`${darkMode ? "dark" : ""} `}>
                <div id="pageRoot" className="w-full h-full bg-surface">
                  <Nav
                    articleData={navData.articles}
                    artistData={navData.artists}
                    exhibitionData={navData.exhibitions}
                  />
                  <NewsletterBox />
                  <Component {...pageProps} />
                </div>
              </div>
            </PurchaseTrackingManagerMachineProvider>
          </ExcessSettlementFundsManagerMachineContextProvider>
        </ArtBlocksProvider>
      </AuthProvider>
    </Web3Provider>
  );
}

const trackPageView = (data: {
  url: string;
  pathname: string;
  query?: object;
}) => {
  analytics.trackPageView(data);
};

AppWrapper.getInitialProps = async (appContext: AppContext) => {
  // calls page's `getInitialProps` and fills `appProps.pageProps`
  const appProps = await NextApp.getInitialProps(appContext);
  let baseUrl = "";

  if (USE_PROXY) {
    baseUrl = AB_FLAGSHIP_URL;
  } else if (appContext.ctx.req?.headers.host) {
    // check the x-forwarded-host first in case we are running behind a proxy
    const host =
      appContext.ctx.req?.headers["x-forwarded-host"] ||
      appContext.ctx.req?.headers.host;

    baseUrl = `${host.includes("localhost") ? "http" : "https"}://${host}`;
  } else if (process.env.NEXT_PUBLIC_HOST_URL) {
    baseUrl = process.env.NEXT_PUBLIC_HOST_URL || "";
  } else if (isBrowser()) {
    const host = window.location.host;
    baseUrl = `${host.includes("localhost") ? "http" : "https"}://${host}`;
  }
  // remove any trailing slashes
  baseUrl = baseUrl.replace(/\/$/, "");

  // add basePath if we have one
  baseUrl = getBasePath() ? `${baseUrl}${getBasePath()}` : baseUrl;

  const navData = await getNavData(baseUrl, appContext);

  return { ...appProps, baseUrl, navData };
};

export const founders = localFont({
  src: [
    {
      path: "../public/fonts/FoundersGrotesk-Light.ttf",
      weight: "300",
      style: "normal",
    },
    {
      path: "../public/fonts/FoundersGrotesk-Regular.ttf",
      weight: "400",
      style: "normal",
    },
    {
      path: "../public/fonts/FoundersGrotesk-Medium.ttf",
      weight: "500",
      style: "normal",
    },
    {
      path: "../public/fonts/FoundersGrotesk-Semibold.ttf",
      weight: "600",
      style: "normal",
    },
    {
      path: "../public/fonts/FoundersGrotesk-LightItalic.ttf",
      weight: "300",
      style: "italic",
    },
    {
      path: "../public/fonts/FoundersGrotesk-RegularItalic.ttf",
      weight: "400",
      style: "italic",
    },
    {
      path: "../public/fonts/FoundersGrotesk-MediumItalic.ttf",
      weight: "500",
      style: "italic",
    },
    {
      path: "../public/fonts/FoundersGrotesk-SemiboldItalic.ttf",
      weight: "600",
      style: "italic",
    },
  ],
  variable: "--font-founders",
  display: "swap",
});

export const sohne = localFont({
  src: [
    {
      path: "../public/fonts/SohneBreit-Kraftig.ttf",
      weight: "400",
      style: "normal",
    },
  ],
  variable: "--font-sohne",
  display: "swap",
});

const NAV_DATA_CACHE_EXPIRATION_SECONDS = 60 * 60 * 24; // 1 day
const NAV_DATA_CACHE_KEY = "navData";

const navDataCache = new NodeCache({
  stdTTL: NAV_DATA_CACHE_EXPIRATION_SECONDS,
  deleteOnExpire: true,
});

async function getNavData(baseUrl: string, appContext: AppContext) {
  const cachedNavData = navDataCache.get(NAV_DATA_CACHE_KEY);
  if (cachedNavData) {
    return cachedNavData;
  }

  let navData: NavDataResponse | null = null;
  try {
    const navDataRes = await fetch(`${baseUrl}/api/nav-data`, {
      headers: {
        Cookie: appContext.ctx.req?.headers.cookie || "",
      },
    });
    navData = await navDataRes.json();
    navDataCache.set(NAV_DATA_CACHE_KEY, navData);
  } catch (error) {
    console.error("Error fetching nav data", error);
    navData = {
      articles: {},
      exhibitions: {},
      artists: [],
    };
  }

  return navData;
}

export default AppWrapper;
